import {bind} from 'bind-decorator';
import {iframeResizer} from 'iframe-resizer';
import * as React from 'react';
import {Redirect} from 'react-router';

import {LxConfig, ROUTES} from 'global/constants';
import uiMessages from 'ui/components/displayMessages';
import {WrappedMessage} from 'utils';
import messages from '../../displayMessages';
import {getHandlerUrlPromise} from '../../utils';
import {XBlockType} from '../BlockEditor/models';
import {BlockData, LoadingState} from './models';
import {createAssetViewedEventForItem, shouldAutoTrackAssetView} from './utils';
import {wrapBlockHtmlForIFrame} from './wrap';

/**
 * Event signals that may be sent by XBlocks to the parent application.
 * Typically these are only used by the studio_view.
 */
export type XBlockNotification = (
    | {eventType: 'save', state: 'end'}
    | {eventType: 'save', state: 'start', message?: string}
    | {eventType: 'error', title?: string, message?: string}
    | {eventType: 'cancel'}
);

interface BlockViewProps {
    usageKey: string;
    title: string;
    blockData: BlockData;
    onNotification?: (event: XBlockNotification) => void;
    autoUpdateIFrameHeight: boolean;
    onInitDone?: () => void;
    onRequestClose?: () => void;
    username?: string;
    classroomName?: string;
}

interface BlockViewState {
    videoViewed: boolean;
}

/**
 * React component that displays an XBlock in a sandboxed IFrame.
 *
 * The IFrame is resized responsively so that it fits the content height.
 *
 * We use an IFrame so that the XBlock code, including user-authored HTML,
 * cannot access things like the user's cookies, nor can it make GET/POST
 * requests as the user. However, it is allowed to call any XBlock handlers.
 */
export class BlockView extends React.Component<BlockViewProps, BlockViewState> {

    public static defaultProps = {
        autoUpdateIFrameHeight: true,
    };

    private iframeRef: React.RefObject<HTMLIFrameElement>;

    constructor(props: BlockViewProps) {
        super(props);
        this.iframeRef = React.createRef();
        this.state = {
            videoViewed: false,
        };
    }

    public componentDidMount() {
        // Prepare to receive messages from the IFrame.
        // Messages are the only way that the code in the IFrame can communicate
        // with the surrounding UI.
        window.addEventListener('message', this.receivedWindowMessage);

        // If the iframe has been setup, init iframeResizer.
        if (this.props.autoUpdateIFrameHeight && this.iframeRef.current) {
            iframeResizer({
                autoResize: true,
                inPageLinks: true, /// To allow the anchor links
            }, this.iframeRef.current);
        }

        if (shouldAutoTrackAssetView(this.props.usageKey)) {
            createAssetViewedEventForItem(this.props.usageKey);
        }
    }

    public componentWillUnmount() {
        window.removeEventListener('message', this.receivedWindowMessage);
        if (this.iframeRef.current) {
            const iframeElement: any =  this.iframeRef.current;
            const elementIframeResizer: any = iframeElement.iFrameResizer;
            if (elementIframeResizer) {
                elementIframeResizer.removeListeners();
            }
        }
    }

    public render() {
        const loadingState = this.props.blockData.loadingState;
        if (loadingState === LoadingState.READY) {
            return (
                <div className='lx-block' dir='auto'>
                    <iframe title={this.props.title} ref={this.iframeRef} src={LxConfig.SecureOriginXBlockBootstrapURL}
                        // allow 'autoplay' is required to allow the video XBlock to control the YouTube iframe it has.
                        // fullscreen is required for the video player to show the video in full screen.
                        allow='accelerometer; autoplay; encrypted-media; fullscreen; gyroscope; picture-in-picture'
                        allowFullScreen={true}  // Needed for Firefox.
                        sandbox={[
                            'allow-forms',
                            'allow-modals',
                            'allow-popups',
                            'allow-popups-to-escape-sandbox',
                            'allow-presentation',
                            'allow-same-origin',  // This is only secure IF the IFrame source
                                                  // is served from a completely different domain name
                                                  // e.g. labxchange-xblocks.net vs www.labxchange.org
                            'allow-scripts',
                            'allow-top-navigation-by-user-activation',
                        ].join(' ')}></iframe>
                </div>
            );
        } else if (loadingState === LoadingState.LOADING) {
            return (<div>
                <WrappedMessage message={uiMessages.uiLoading} />
            </div>);
        } else if (loadingState === LoadingState.UNAUTHORIZED) {
            return <Redirect to={ROUTES.General.SIGN_IN} push/>;
        } else {
            return (<div>
                <WrappedMessage message={messages.libraryContentError} />
            </div>);
        }
    }

    private initialHtml() {
        const viewData = this.props.blockData.viewData!;

        /// This is to add 'dir="auto"' to the questions blocks to render arabic font on right align
        const content = viewData.content.replace(/class=&#34;problem&#34;/gi, 'class=&#34;problem&#34; dir=&#34;auto&#34;');

        const urlResources = viewData.resources.filter((r) => r.kind === 'url');
        const html = wrapBlockHtmlForIFrame(
            content,
            urlResources.filter((r) => r.mimetype === 'application/javascript').map((r) => r.data),
            urlResources.filter((r) => r.mimetype === 'text/css').map((r) => r.data),
            this.shouldInitializeXBlockRuntime(this.props.usageKey),
            {
                username: this.props.username,
                classroomName: this.props.classroomName,
            }
        );
        return html;
    }

    /*
    Only some XBlocks need the Javascript runtime.
    To speed up loading time we only load static files for these XBlocks.
     */
    private shouldInitializeXBlockRuntime(usageKey: string) {
        return usageKey.search(XBlockType.Audio) >= 0
            || usageKey.search(XBlockType.CaseStudy) >= 0
            || usageKey.search(XBlockType.DragAndDrop) >= 0
            || usageKey.search(XBlockType.Problem) >= 0
            || usageKey.search(XBlockType.TeachingGuide) >= 0
            || usageKey.search(XBlockType.AnnotatedVideo) >= 0
            || usageKey.search(XBlockType.Video) >= 0
            || usageKey.search(XBlockType.LXVideo) >= 0
            || usageKey.search('unit') >= 0;
    }

    /**
     * Handle any messages we receive from the XBlock Runtime code in the IFrame.
     * See wrap.ts to see the code that sends these messages.
     */
    @bind private async receivedWindowMessage(event: MessageEvent) {
        if (this.iframeRef.current === null || event.source !== this.iframeRef.current.contentWindow ||
            event.data.method === undefined) {
            return; // This is some other random message.
        }
        const {method, replyKey, ...args} = event.data;
        const frame = this.iframeRef.current.contentWindow!;
        const sendReply = async (data: any) => {
            frame.postMessage({...data, replyKey}, '*');
        };
        if (method === 'bootstrap') {
            sendReply({initialHtml: this.initialHtml()});
        } else if (method === 'get_handler_url') {
            if (args.usageId === this.props.usageKey) {
                sendReply({
                    handlerUrl: this.props.blockData.handlerUrl,
                });
            } else {
                sendReply({
                    handlerUrl: await this.getSecureHandlerUrl(args.usageId),
                });
            }
        } else if (method === 'init_done') {
            if (this.props.onInitDone) {
                this.props.onInitDone();
            }
        } else if (method === 'event') {
            if (args.data.type === 'requestClose') {
                // Allow the iframed content to close the modal.
                if (this.props.onRequestClose) {
                    this.props.onRequestClose();
                }
            }
        } else if (method.indexOf('xblock:') === 0) {
            // This is a notification from the XBlock's frontend via 'runtime.notify(event, args)'
            if (this.props.onNotification) {
                this.props.onNotification({
                    eventType: method.substr(7), // Remove the 'xblock:' prefix that we added in wrap.ts
                    ...args,
                });
            }
        }
    }

    /**
     * Helper method which gets a "secure handler URL" from the LabXchange backend
     * A "secure handler URL" is a URL that the XBlock runtime can use even from
     * within its sandboxed IFrame. (The IFrame is considered a different origin,
     * and normally, cross-origin handler requests would be blocked).
     *
     * @param usageKey The usage key of the XBlock whose handlers you want to call.
     */
    private async getSecureHandlerUrl(usageKey: string) {
        const data = await getHandlerUrlPromise(usageKey);
        return data.handlerUrl;
    }
}
